import React, { useEffect, useMemo, useState } from 'react';
import { fetchCompanyFeatures, upsertCompanyFeature } from '../../services/companyCustomizationApi';
import type { CompanyFeature } from '../../services/companyCustomizationApi';
import { safeJsonParse, toPrettyJson } from '../../utils/json';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyFeaturesManagerProps {
  companyId: string;
}

const emptyFormState = {
  featureKey: '',
  enabled: true,
  configText: '',
};

const CompanyFeaturesManager: React.FC<CompanyFeaturesManagerProps> = ({ companyId }) => {
  const { t } = useI18n();
  const [features, setFeatures] = useState<CompanyFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState(emptyFormState);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const filteredFeatures = useMemo(() => {
    if (!filter.trim()) {
      return features;
    }
    return features.filter((feature) =>
      feature.featureKey.toLowerCase().includes(filter.trim().toLowerCase())
    );
  }, [features, filter]);

  const loadFeatures = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyFeatures(companyId);
      setFeatures(data);
    } catch (err) {
      console.error('[CompanyFeaturesManager] Failed to load features', err);
      setError('Não foi possível carregar os feature flags da empresa.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const resetFeedback = () => {
    setError(null);
    setSuccess(null);
  };

  const handleEdit = (feature: CompanyFeature) => {
    resetFeedback();
    setForm({
      featureKey: feature.featureKey,
      enabled: feature.enabled,
      configText: toPrettyJson(feature.config),
    });
  };

  const handleToggle = async (feature: CompanyFeature) => {
    resetFeedback();
    setSavingKey(feature.featureKey);
    try {
      await upsertCompanyFeature(companyId, {
        featureKey: feature.featureKey,
        enabled: !feature.enabled,
        config: feature.config,
      });
      await loadFeatures();
      setSuccess(`Feature "${feature.featureKey}" atualizada.`);
    } catch (err) {
      console.error('[CompanyFeaturesManager] Failed to toggle feature', err);
      setError('Não foi possível atualizar o feature flag.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (!form.featureKey.trim()) {
      setError('Informe o identificador do feature flag.');
      return;
    }

    let parsedConfig: Record<string, unknown> = {};
    try {
      parsedConfig =
        form.configText.trim() === '' ? {} : safeJsonParse(form.configText, parsedConfig);
    } catch {
      setError(t('customization.features.invalidJson'));
      return;
    }

    setSavingKey(form.featureKey);
    try {
      await upsertCompanyFeature(companyId, {
        featureKey: form.featureKey.trim(),
        enabled: form.enabled,
        config: parsedConfig,
      });
      await loadFeatures();
      setForm(emptyFormState);
      setSuccess('Feature flag salva com sucesso.');
    } catch (err) {
      console.error('[CompanyFeaturesManager] Failed to save feature', err);
      setError(t('customization.features.errorSave'));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '16px',
          border: '1px solid #e5e7eb',
          borderRadius: '10px',
          backgroundColor: '#f9fafb',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Feature Flags</h3>
          <input
            type="search"
            placeholder="Filtrar por chave..."
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
          <button
            type="button"
            onClick={loadFeatures}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
            disabled={loading}
          >
            {loading ? 'Atualizando...' : 'Atualizar lista'}
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 12px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#b91c1c',
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              padding: '10px 12px',
              backgroundColor: '#dcfce7',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              color: '#15803d',
            }}
          >
            {success}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {loading ? (
            <div style={{ padding: '20px', color: '#6b7280' }}>{t('customization.features.loading')}</div>
          ) : filteredFeatures.length === 0 ? (
            <div style={{ padding: '20px', color: '#6b7280' }}>{t('customization.features.none')}</div>
          ) : (
            filteredFeatures.map((feature) => (
              <div
                key={feature.featureKey}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <div>
                    <strong style={{ fontSize: '14px' }}>{feature.featureKey}</strong>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Atualizado em{' '}
                      {feature.updatedAt
                        ? new Date(feature.updatedAt).toLocaleString()
                        : '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle(feature)}
                    disabled={savingKey === feature.featureKey}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '999px',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: feature.enabled ? '#dcfce7' : '#fee2e2',
                      color: feature.enabled ? '#166534' : '#b91c1c',
                      fontWeight: 600,
                    }}
                  >
                    {feature.enabled ? 'Ativo' : 'Inativo'}
                  </button>
                </div>
                {Object.keys(feature.config || {}).length > 0 && (
                  <pre
                    style={{
                      margin: 0,
                      fontSize: '12px',
                      maxHeight: '160px',
                      overflow: 'auto',
                      backgroundColor: '#f3f4f6',
                      padding: '10px',
                      borderRadius: '6px',
                    }}
                  >
                    {toPrettyJson(feature.config)}
                  </pre>
                )}
                <button
                  type="button"
                  onClick={() => handleEdit(feature)}
                  style={{
                    alignSelf: 'flex-start',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >{t('actions.edit')}</button>
              </div>
            ))
          )}
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
            Identificador do Feature *
            <input
              type="text"
              value={form.featureKey}
              onChange={(event) => setForm({ ...form, featureKey: event.target.value })}
              placeholder="ex: dashboard.advancedWidgets"
              style={{
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
              }}
              required
            />
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', flex: 1 }}
          >
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
            />{t('customization.features.enabled')}</label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '14px' }}>
            Configuração avançada (JSON)
            <textarea
              value={form.configText}
              onChange={(event) => setForm({ ...form, configText: event.target.value })}
              rows={6}
              placeholder='{"max_slots": 3}'
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontFamily: 'monospace',
                fontSize: '13px',
              }}
            />
          </label>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            Use JSON para definir limites, modos avançados ou integrações específicas.
          </span>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={savingKey !== null}
            style={{
              padding: '10px 18px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#2563eb',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {savingKey ? 'Salvando...' : 'Salvar Feature'}
          </button>
          <button
            type="button"
            onClick={() => {
              resetFeedback();
              setForm(emptyFormState);
            }}
            style={{
              padding: '10px 18px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >{t('customization.features.clearForm')}</button>
        </div>
      </form>
    </div>
  );
};

export default CompanyFeaturesManager;

